import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  TableBody,
  TableHead,
  TableRow,
  Box,
  Typography,
  useMediaQuery,
  useTheme,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CloseIcon from '@mui/icons-material/Close';
import { styled } from '@mui/material/styles';

export interface ColumnGroup {
  id: string;
  Header: string;
  columns: GroupedColumn[];
}

export interface GroupedColumn {
  accessor: string;
  Header: string | React.ReactNode;
  width?: string;
  Cell?: ({ value, row }: { value: any; row: any }) => React.ReactNode;
}

export interface GroupedTableProps<T extends object> {
  data: T[];
  columnGroups: ColumnGroup[];
  moduleName?: string;
  viewMode?: boolean;
  disableAutoSizing?: boolean;
  pageSize?: number;
  lazyLoad?: boolean;
  lazyLoadHeight?: number;
  lazyLoadingBatchSize?: number;
  isLoading?: boolean;
  rowClassName?: (row: T) => string;
  rowBackgroundColor?: string | ((row: T) => string);
  summaryRows?: {
    subTotalRow?: any;
    weightProportionsAndIpkRow?: any;
  };
}

// Styled components similar to StatusTable
const StyledTableContainer = styled('div')(() => ({
  maxHeight: '70vh',
  overflowX: 'auto',
  overflowY: 'auto',
  border: '1px solid #E1E8ED',
  borderRadius: '8px',
}));

const StyledPaper = styled('div')(() => ({
  width: '100%',
  overflow: 'hidden',
  backgroundColor: '#FFFFFF',
}));

const StyledTable = styled('table')(() => ({
  width: '100%',
  borderCollapse: 'separate',
  borderSpacing: '1px',
  backgroundColor: '#F5F5F5',
}));

const TableWrapper = styled(Box)(() => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
}));

const TableTitle = styled(Typography)(() => ({
  fontSize: '14px',
  fontWeight: 600,
  color: '#063142',
  marginBottom: '8px',
}));

// Header cells similar to StatusTable
const HeaderCell = styled('th')(({ theme }) => ({
  fontWeight: 700,
  fontSize: '12px',
  backgroundColor: '#E7EFF5 !important',
  textAlign: 'center',
  color: '#063142',
  padding: theme.spacing(1, 1.5),
  position: 'sticky',
  zIndex: 1300,
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  border: 'none',
}));

const SubHeaderCell = styled('th')(({ theme }) => ({
  fontWeight: 600,
  fontSize: '11px',
  backgroundColor: '#E7EFF5 !important',
  textAlign: 'center',
  color: '#063142',
  padding: '8px 12px',
  position: 'relative',
  border: 'none',
  verticalAlign: 'middle',
}));

const DataCell = styled('td')(({ theme }) => ({
  fontSize: '11px',
  textAlign: 'center',
  backgroundColor: '#FFFFFF',
  border: 'none',
  padding: theme.spacing(0.75, 1.5),
  '&.number-cell': {
    fontFamily: 'monospace',
    textAlign: 'right',
    fontWeight: 500,
  },
}));

const SummaryCell = styled('td')(({ theme }) => ({
  fontSize: '11px',
  textAlign: 'center',
  backgroundColor: '#F8F9FA',
  border: 'none',
  padding: theme.spacing(0.75, 1.5),
  fontWeight: 600,
  '&.number-cell': {
    fontFamily: 'monospace',
    textAlign: 'right',
    fontWeight: 600,
  },
  '&.subtotal-row': {
    backgroundColor: '#E3F2FD',
    borderTop: '2px solid #1976D2',
  },
  '&.proportion-row': {
    backgroundColor: '#FFF3E0',
    borderTop: '1px solid #FF9800',
  },
}));

const SummaryRowLabel = styled('td')(({ theme }) => ({
  fontSize: '12px',
  textAlign: 'left',
  backgroundColor: '#F8F9FA',
  border: 'none',
  padding: theme.spacing(0.75, 1.5),
  fontWeight: 700,
  color: '#063142',
  '&.subtotal-row': {
    backgroundColor: '#E3F2FD',
    borderTop: '2px solid #1976D2',
  },
  '&.proportion-row': {
    backgroundColor: '#FFF3E0',
    borderTop: '1px solid #FF9800',
  },
}));

const SortMenu = styled(Menu)({
  '& .MuiPaper-root': {
    minWidth: '250px',
    maxWidth: '300px',
    boxShadow: '0px 2px 8px rgba(0,0,0,0.15)',
    borderRadius: '8px',
  }
});

const SortMenuItem = styled(MenuItem)({
  fontSize: '12px',
  fontFamily: "'Lato', sans-serif",
  padding: '10px 16px',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  '& .MuiSvgIcon-root': {
    fontSize: '16px',
  },
  '&:hover': {
    backgroundColor: '#f5f5f5',
  }
});

// Helper function to calculate text width
function calculateTextWidth(text: string, font: string): number {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (context) {
    context.font = font;
    return context.measureText(text).width;
  }
  return text.length * 8; // Fallback
}

// Debounce function
function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number,
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

/**
 * GroupedTable component with column grouping support similar to StatusTable
 * 
 * This component extends the regular Table functionality to support
 * grouped column headers, making it ideal for reports like CN54 where
 * related columns need to be visually grouped together.
 */
function GroupedTable<T extends object>({
  data = [],
  columnGroups = [],
  moduleName,
  viewMode = false,
  disableAutoSizing = false,
  pageSize = 25,
  lazyLoad = false,
  lazyLoadHeight = 400,
  lazyLoadingBatchSize = 25,
  isLoading = false,
  rowClassName,
  rowBackgroundColor,
  summaryRows,
  ...props
}: GroupedTableProps<T>): React.ReactElement {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [columnWidths, setColumnWidths] = useState<Record<string, string>>({});
  const [isCalculating, setIsCalculating] = useState(true);
  const [sortConfig, setSortConfig] = useState<{ column: string; direction: 'ASC' | 'DESC' } | undefined>(undefined);
  const [sortMenuAnchor, setSortMenuAnchor] = useState<null | HTMLElement>(null);
  const [activeColumn, setActiveColumn] = useState<string | null>(null);

  // Flatten all columns from groups for easier access
  const allColumns = useMemo(() => {
    return columnGroups.reduce<GroupedColumn[]>((acc, group) => {
      return [...acc, ...group.columns];
    }, []);
  }, [columnGroups]);

  // Calculate column widths based on content
  const calculateColumnWidths = useCallback(() => {
    if (!tableContainerRef.current || data.length === 0 || disableAutoSizing) {
      setIsCalculating(false);
      return;
    }

    const newColumnWidths: Record<string, number> = {};
    const headerFont = '700 12px "Lato", sans-serif';
    const cellFont = '400 12px "Lato", sans-serif';

    // Define min and max column widths
    const minColWidth = isMobile ? 60 : 80;
    const maxColWidth = isMobile ? 120 : 180;

    // Calculate width for each column
    allColumns.forEach(column => {
      // Start with header width - handle both string and React.ReactNode
      const headerText = typeof column.Header === 'string' ? column.Header : String(column.accessor);
      let maxWidth = Math.max(calculateTextWidth(headerText, headerFont) + 16, minColWidth);

      // Sample data to get better width estimates
      data.slice(0, 10).forEach(row => {
        const value = (row as any)[column.accessor];
        let displayValue = '';
        
        if (column.Cell) {
          // For custom cells, use a rough estimate
          displayValue = String(value || '').substring(0, 15);
        } else if (typeof value === 'number') {
          // Format numbers properly
          if (column.accessor.toLowerCase().includes('weight')) {
            displayValue = value.toFixed(3);
          } else if (column.accessor.toLowerCase().includes('items') || column.accessor.toLowerCase().includes('nof')) {
            displayValue = value.toLocaleString();
          } else {
            displayValue = value.toLocaleString();
          }
        } else {
          // For strings or other types, use as-is
          displayValue = String(value || '');
        }
        
        const contentWidth = calculateTextWidth(displayValue, cellFont) + 16;
        maxWidth = Math.max(maxWidth, contentWidth);
      });

      // Apply min/max constraints
      newColumnWidths[column.accessor] = Math.min(Math.max(maxWidth, minColWidth), maxColWidth);
    });

    // Convert numeric widths to strings with 'px' suffix
    const finalColumnWidths: Record<string, string> = {};
    for (const [key, value] of Object.entries(newColumnWidths)) {
      finalColumnWidths[key] = `${value}px`;
    }

    setColumnWidths(finalColumnWidths);
    setIsCalculating(false);
  }, [data, allColumns, disableAutoSizing, isMobile]);

  // Debounced version of the calculation function
  const debouncedCalculateColumnWidths = debounce(calculateColumnWidths, 300);

  // Calculate column widths on initial render and when data changes
  useEffect(() => {
    setIsCalculating(true);
    calculateColumnWidths();
  }, [data, columnGroups]);

  // Recalculate on window resize
  useEffect(() => {
    const handleResize = () => {
      setIsCalculating(true);
      debouncedCalculateColumnWidths();
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [debouncedCalculateColumnWidths]);

  // Helper function to format numbers
  const formatNumber = (value: any, columnAccessor?: string): string => {
    if (value === null || value === undefined || value === '') return '';
    
    // If value is already a string, return it as-is (from backend)
    if (typeof value === 'string') {
      return value;
    }
    
    // Only apply number formatting if value is actually a number
    if (typeof value === 'number') {
      // Special handling for Total Gross Weight - always toFixed(1)
      if (columnAccessor?.toLowerCase().includes('totalgrossweight')) {
        return value.toFixed(1);
      }
      
      // Other weight fields - toFixed(3)
      if (columnAccessor?.toLowerCase().includes('weight')) {
        return value.toFixed(3);
      }
      
      // Items/count fields - show as integers
      if (columnAccessor?.toLowerCase().includes('items') || columnAccessor?.toLowerCase().includes('nof')) {
        return Math.round(value).toString();
      }
      
      // For other numbers - show as integers if they are whole numbers, otherwise with decimals
      if (value % 1 === 0) {
        return Math.round(value).toString();
      }
      
      // For decimal values, use appropriate precision
      return value.toString();
    }
    
    // For any other type, convert to string
    return String(value);
  };

  // Determine raw (sortable) accessor for formatted fields
  const getSortableAccessor = (accessor: string): string => {
    if (accessor.endsWith('Formatted')) return accessor.replace(/Formatted$/, '');
    return accessor;
  };

  // Parse potential numeric value from mixed input
  const parseNumeric = (value: unknown): number | null => {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string') {
      const cleaned = value.replace(/[',`\s]/g, '');
      const num = Number(cleaned);
      if (Number.isFinite(num)) return num;
    }
    return null;
  };

  // Sorted data memo
  const sortedData = useMemo(() => {
    if (!sortConfig) return data;
    const { column, direction } = sortConfig;
    const multiplier = direction === 'ASC' ? 1 : -1;
    const sortableAccessor = getSortableAccessor(column);
    return [...data].sort((a: any, b: any) => {
      const av = a?.[sortableAccessor] ?? a?.[column];
      const bv = b?.[sortableAccessor] ?? b?.[column];
      if (av == null && bv == null) return 0;
      if (av == null) return -1 * multiplier;
      if (bv == null) return 1 * multiplier;
      const an = parseNumeric(av);
      const bn = parseNumeric(bv);
      if (an !== null && bn !== null) return (an - bn) * multiplier;
      const astr = String(av);
      const bstr = String(bv);
      return astr.localeCompare(bstr, undefined, { numeric: true, sensitivity: 'base' }) * multiplier;
    });
  }, [data, sortConfig]);

  const handleHeaderSort = useCallback((accessor: string, event?: React.MouseEvent<HTMLElement>) => {
    if (event) {
      event.stopPropagation();
      setActiveColumn(accessor);
      setSortMenuAnchor(event.currentTarget);
    } else {
      // Direct sort on icon click
      setSortConfig(prev => {
        if (!prev || prev.column !== accessor) return { column: accessor, direction: 'ASC' };
        if (prev.direction === 'ASC') return { column: accessor, direction: 'DESC' };
        return undefined;
      });
    }
  }, []);

  const handleSortSelect = useCallback((direction: 'ASC' | 'DESC') => {
    if (activeColumn) {
      setSortConfig({ column: activeColumn, direction });
    }
    setSortMenuAnchor(null);
  }, [activeColumn]);

  const renderSortIcon = (accessor: string) => {
    if (sortConfig && sortConfig.column === accessor) {
      return sortConfig.direction === 'ASC' ? (
        <ArrowUpwardIcon sx={{ fontSize: 12, color: '#1976D2' }} />
      ) : (
        <ArrowDownwardIcon sx={{ fontSize: 12, color: '#1976D2' }} />
      );
    }
    return <MoreVertIcon sx={{ fontSize: 12, color: 'inherit' }} />;
  };

  // Render cell content
  const renderCell = (row: T, column: GroupedColumn) => {
    const value = (row as any)[column.accessor];
    
    if (column.Cell) {
      return column.Cell({ value, row });
    }
    
    // Use formatNumber for all values - it will handle strings and numbers appropriately
    return formatNumber(value, column.accessor);
  };

  // Function to determine if a column contains numeric data
  const isNumericColumn = (accessor: string): boolean => {
    return accessor.toLowerCase().includes('weight') || 
           accessor.toLowerCase().includes('items') || 
           accessor.toLowerCase().includes('nof');
  };

  // Show loading state
  if (isLoading || isCalculating) {
    return (
      <TableWrapper>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
          <CircularProgress size={40} />
          <Typography sx={{ mt: 2, color: '#666' }}>
            {isCalculating ? 'Calculating layout...' : 'Loading data...'}
          </Typography>
        </Box>
      </TableWrapper>
    );
  }

  // Show empty state
  if (data.length === 0) {
    return (
      <TableWrapper>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
          <Typography sx={{ color: '#666', fontStyle: 'italic' }}>
            No data available
          </Typography>
        </Box>
      </TableWrapper>
    );
  }

  return (
    <TableWrapper>
      <StyledPaper>
        <StyledTableContainer ref={tableContainerRef}>
          <StyledTable>
            <TableHead>
              {/* Group Headers Row */}
              <TableRow sx={{ 
                '& th': { 
                  px: 1.5, 
                  py: 1,
                  position: 'sticky',
                  top: 0,
                  zIndex: 1300,
                  backgroundColor: '#E7EFF5 !important',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                } 
              }}>
                {columnGroups.map(group => {
                  // Only render group header if it has content
                  if (!group.Header) {
                    // For groups without headers, render individual column headers with rowSpan=2
                    return group.columns.map(column => (
                      <HeaderCell
                        key={column.accessor}
                        rowSpan={2}
                        style={{ 
                          width: columnWidths[column.accessor],
                          textAlign: 'center',
                          position: 'relative'
                        }}
                      >
                        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', pr: 2 }}>
                          <Typography sx={{ fontSize: '12px', fontWeight: 700 }}>
                            {column.Header as any}
                          </Typography>
                          <IconButton
                            size='small'
                            onClick={(e) => {
                              e.stopPropagation();
                              handleHeaderSort(column.accessor, e);
                            }}
                            sx={{
                              position: 'absolute',
                              right: -4,
                              top: '50%',
                              backgroundColor: sortConfig && sortConfig.column === column.accessor
                                ? '#E3F2FD !important' 
                                : '#E7EFF5 !important',
                              transform: 'translateY(-50%)',
                              padding: isMobile ? '1px' : '2px',
                              border: sortConfig && sortConfig.column === column.accessor
                                ? '1px solid #2196F3' 
                                : 'none',
                              '&:hover': {
                                backgroundColor: sortConfig && sortConfig.column === column.accessor
                                  ? '#FFEBEE !important'
                                  : '#E3F2FD !important',
                              },
                            }}
                          >
                            {renderSortIcon(column.accessor)}
                          </IconButton>
                        </Box>
                      </HeaderCell>
                    ));
                  } else {
                    // For groups with headers, render group header normally
                    return (
                      <HeaderCell
                        key={group.id}
                        colSpan={group.columns.length}
                        style={{ textAlign: 'center' }}
                      >
                        {group.Header}
                      </HeaderCell>
                    );
                  }
                })}
              </TableRow>
              
              {/* Individual Column Headers Row - only for groups that have group headers */}
              <TableRow sx={{ 
                '& th': { 
                  backgroundColor: '#E7EFF5 !important',
                } 
              }}>
                {columnGroups.map(group => {
                  // Only render column headers for groups that have group headers
                  if (group.Header) {
                    return group.columns.map(column => (
                      <SubHeaderCell
                        key={column.accessor}
                        style={{ 
                          width: columnWidths[column.accessor],
                          textAlign: 'center',
                          position: 'relative'
                        }}
                      >
                        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', pr: 2 }}>
                          <Typography sx={{ fontSize: '11px', fontWeight: 600 }}>
                            {column.Header as any}
                          </Typography>
                          <IconButton
                            size='small'
                            onClick={(e) => {
                              e.stopPropagation();
                              handleHeaderSort(column.accessor, e);
                            }}
                            sx={{
                              position: 'absolute',
                              right: -4,
                              top: '50%',
                              backgroundColor: sortConfig && sortConfig.column === column.accessor
                                ? '#E3F2FD !important' 
                                : '#E7EFF5 !important',
                              transform: 'translateY(-50%)',
                              padding: isMobile ? '1px' : '2px',
                              border: sortConfig && sortConfig.column === column.accessor
                                ? '1px solid #2196F3' 
                                : 'none',
                              '&:hover': {
                                backgroundColor: sortConfig && sortConfig.column === column.accessor
                                  ? '#FFEBEE !important'
                                  : '#E3F2FD !important',
                              },
                            }}
                          >
                            {renderSortIcon(column.accessor)}
                          </IconButton>
                        </Box>
                      </SubHeaderCell>
                    ));
                  }
                  return null; // Skip columns that already have rowSpan=2 headers
                })}
              </TableRow>
            </TableHead>
            
            <TableBody>
              {sortedData.map((row: any, index: number) => (
                 <TableRow 
                   key={row.id || index} 
                   sx={{ '& td': { px: 1.5, py: 0.75 } }}
                 >
                   {allColumns.map(column => {
                     const isNumeric = isNumericColumn(column.accessor);
                     
                     return (
                       <DataCell
                         key={column.accessor}
                         className={isNumeric ? 'number-cell' : ''}
                         style={{
                           backgroundColor: rowBackgroundColor 
                             ? typeof rowBackgroundColor === 'function' 
                               ? rowBackgroundColor(row)
                               : rowBackgroundColor
                             : '#FFFFFF'
                         }}
                       >
                         {renderCell(row, column)}
                       </DataCell>
                     );
                   })}
                 </TableRow>
              ))}
               
               {/* Summary Rows */}
               {summaryRows?.subTotalRow && (
                 <TableRow sx={{ '& td': { px: 1.5, py: 0.75 } }}>
                   {/* Label spanning first columns - different for CN10 */}
                   <SummaryCell
                     className="subtotal-row"
                     colSpan={moduleName?.startsWith('CN10') ? 3 : 4}
                   >
                     {moduleName?.startsWith('CN10') ? 'Total to be forwarded to CN19' : 'Subtotals'}
                   </SummaryCell>
                   
                   {/* Data for remaining columns - from 4th for CN10, 5th for others */}
                   {allColumns.slice(moduleName?.startsWith('CN10') ? 3 : 4).map(column => {
                     const isNumeric = isNumericColumn(column.accessor);
                     const subTotalKey = `${column.accessor}SubTotal`;
                     let value = summaryRows.subTotalRow[subTotalKey] || summaryRows.subTotalRow[column.accessor];
                     
                     // Extract mail format from moduleName to determine display rules
                     const mailFormat = moduleName ? moduleName.split('-')[1] : '';
                     
                     // For non-X mail formats, hide Estimated Gross Weight columns
                     if (mailFormat !== 'X') {
                       if (column.accessor === 'psEstimatedGrossWeight' || 
                           column.accessor === 'gestimatedGrossWeight' || 
                           column.accessor === 'eestimatedGrossWeight') {
                         value = undefined; // Hide these values for non-X formats
                       }
                     }
                     
                     return (
                       <SummaryCell
                         key={column.accessor}
                         className={`subtotal-row ${isNumeric ? 'number-cell' : ''}`}
                       >
                         {value !== undefined ? formatNumber(value, column.accessor) : ''}
                       </SummaryCell>
                     );
                   })}
                 </TableRow>
               )}
               
               {summaryRows?.weightProportionsAndIpkRow && !moduleName?.startsWith('CN10') && (
                 <TableRow sx={{ '& td': { px: 1.5, py: 0.75 } }}>
                   {/* Label spanning first 3 columns */}
                   <SummaryCell
                     className="proportion-row"
                     colSpan={3}
                   >
                     Weight Proportions and IPK
                   </SummaryCell>
                   
                   {/* IPK label in Sampled Tare Weight column */}
                   <SummaryCell className="proportion-row">
                     IPK
                   </SummaryCell>
                   
                   {/* Data for remaining columns (from 5th column onwards) */}
                   {allColumns.slice(4).map((column, columnIndex) => {
                     const isNumeric = isNumericColumn(column.accessor);
                     const proportionData = summaryRows.weightProportionsAndIpkRow;
                     
                     // Helper function to format IPK values
                     const formatIpkValue = (ipkValue: any): string => {
                       // If it's already a string, return as-is
                       if (typeof ipkValue === 'string') {
                         return ipkValue;
                       }
                       
                       // If it's a number, format it
                       if (typeof ipkValue === 'number') {
                         if (ipkValue % 1 === 0) {
                           return ipkValue.toFixed(1);
                         }
                         return parseFloat(ipkValue.toFixed(4)).toString();
                       }
                       
                       // For any other type, convert to string
                       return String(ipkValue || '');
                     };
                     
                     // Extract mail format from moduleName to determine display rules
                     const mailFormat = moduleName ? moduleName.split('-')[1] : '';
                     
                     // Map column accessors to weight proportion and IPK values
                     let value = '';
                     let colSpan = 1;
                     let skipCell = false;
                     
                     // Handle different display logic for mail format X vs others
                     if (mailFormat === 'X') {
                       // For mail format X: Show text labels and actual values, with merged columns
                       // PS/Format P columns
                       if (column.accessor === 'psSampledNofItems') {
                         value = 'Weight Proportion';
                         colSpan = 2; // Merge with Sampled Net Weight column
                       } else if (column.accessor === 'psSampledNetWeight') {
                         skipCell = true; // Skip this cell as it's merged with previous
                       } else if (column.accessor === 'psCalculatedSampledGrossWeight') {
                         value = proportionData.psWeightProportion !== undefined ? `${proportionData.psWeightProportion}%` : '';
                       } else if (column.accessor === 'psEstimatedGrossWeight') {
                         value = 'IPK';
                       } else if (column.accessor === 'psEstimatedNofItems') {
                         value = proportionData.psIpk !== undefined ? formatIpkValue(proportionData.psIpk) : '';
                       }
                       // G/Format G columns
                       else if (column.accessor === 'gsampledNofItems') {
                         value = 'Weight Proportion';
                         colSpan = 2; // Merge with Sampled Net Weight column
                       } else if (column.accessor === 'gsampledNetWeight') {
                         skipCell = true; // Skip this cell as it's merged with previous
                       } else if (column.accessor === 'gcalculatedSampledGrossWeight') {
                         value = proportionData.gweightProportion !== undefined ? `${proportionData.gweightProportion}%` : '';
                       } else if (column.accessor === 'gestimatedGrossWeight') {
                         value = 'IPK';
                       } else if (column.accessor === 'gestimatedNofItems') {
                         value = proportionData.gipk !== undefined ? formatIpkValue(proportionData.gipk) : '';
                       }
                       // E/Format E columns
                       else if (column.accessor === 'esampledNofItems') {
                         value = 'Weight Proportion';
                         colSpan = 2; // Merge with Sampled Net Weight column
                       } else if (column.accessor === 'esampledNetWeight') {
                         skipCell = true; // Skip this cell as it's merged with previous
                       } else if (column.accessor === 'ecalculatedSampledGrossWeight') {
                         value = proportionData.eweightProportion !== undefined ? `${proportionData.eweightProportion}%` : '';
                       } else if (column.accessor === 'eestimatedGrossWeight') {
                         value = 'IPK';
                       } else if (column.accessor === 'eestimatedNofItems') {
                         value = proportionData.eipk !== undefined ? formatIpkValue(proportionData.eipk) : '';
                       }
                       // Note: totalGrossWeight remains empty for format X
                     } else {
                       // For other mail formats (P, G, E, S, J, Z): Only show totalGrossWeight IPK
                       if (column.accessor === 'totalGrossWeight') {
                         value = proportionData.ipk !== undefined ? formatIpkValue(proportionData.ipk) : '';
                       }
                       // Note: weight proportions and IPK values in P/S, G, E columns are NOT shown for other formats
                     }
                     
                     // Skip merged cells
                     if (skipCell) {
                       return null;
                     }
                     
                     return (
                       <SummaryCell
                         key={column.accessor}
                         className={`proportion-row ${isNumeric ? 'number-cell' : ''}`}
                         colSpan={colSpan}
                       >
                         {value}
                       </SummaryCell>
                     );
                   })}
                 </TableRow>
               )}
             </TableBody>
          </StyledTable>
        </StyledTableContainer>
      </StyledPaper>

      {/* Sort Menu */}
      <SortMenu
        anchorEl={sortMenuAnchor}
        open={Boolean(sortMenuAnchor)}
        onClose={() => setSortMenuAnchor(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <SortMenuItem 
          onClick={() => handleSortSelect('ASC')} 
          data-testid="sort-ascending"
        >
          <ArrowUpwardIcon />
          Sort Ascending
        </SortMenuItem>
        <SortMenuItem 
          onClick={() => handleSortSelect('DESC')} 
          data-testid="sort-descending"
        >
          <ArrowDownwardIcon />
          Sort Descending
        </SortMenuItem>
        
        {/* Clear Sort Option - only show if there's an active sort on this column */}
        {sortConfig && sortConfig.column === activeColumn && (
          <SortMenuItem 
            onClick={() => {
              setSortConfig(undefined);
              setSortMenuAnchor(null);
            }} 
            data-testid="clear-sort"
            sx={{
              backgroundColor: 'rgba(244, 67, 54, 0.04)',
              '&:hover': {
                backgroundColor: 'rgba(244, 67, 54, 0.08)',
              }
            }}
          >
            <CloseIcon sx={{ color: '#F44336', fontSize: 16 }} />
            Clear Sort
          </SortMenuItem>
        )}
      </SortMenu>
    </TableWrapper>
  );
}

export default GroupedTable;

